/**
 * Options for the lesson editor's "reflects on role play" picker, and the reading of an import
 * preview's unresolved references, which the import hooks and the dependency dialog share.
 *
 * A reflection lesson points at the scenario it debriefs (`reflects_on_scenario_id`). The link is
 * what feeds the coach that learner's transcript; without it the lesson imports and runs as an
 * ordinary lesson and the coach silently has nothing to reflect on.
 *
 * The editor already loads `/backoffice/v1/courses/<id>/`, whose units carry `content_items` with
 * a `content_type` discriminator, so the option list is derived from data that is already in
 * memory rather than fetched again.
 */

/**
 * The import-preview response key, spelled exactly as the server spells it.
 *
 * Kept as a named constant because getting it wrong is silent in both directions: the dialog simply
 * never opens, the import posts with no resolutions, and the server answers 422 — while any test
 * that builds its own fixture with the same wrong key passes happily.
 *
 * Server: `preview_lesson_import` (backoffice/lessons.py) and `preview_unit_import`
 * (backoffice/unit_import_export.py).
 */
export const UNRESOLVED_REFERENCES_KEY = "unresolved_content_references";

/** The unresolved refs in a preview response, always an array. */
export const readUnresolvedReferences = (previewData) =>
  Array.isArray(previewData?.[UNRESOLVED_REFERENCES_KEY])
    ? previewData[UNRESOLVED_REFERENCES_KEY]
    : [];

/**
 * Whether a preview needs the editor to answer something before importing.
 *
 * Every preview endpoint computes this itself (`requires_user_input`), so prefer the server's own
 * verdict over re-deriving it here — re-deriving lets a renamed key go unnoticed. The array
 * checks are a fallback for a response without the flag.
 */
export const previewRequiresUserInput = (previewData) => {
  if (typeof previewData?.requires_user_input === "boolean") {
    return previewData.requires_user_input;
  }
  const conflicts = Array.isArray(previewData?.dependency_conflicts)
    ? previewData.dependency_conflicts
    : [];
  return conflicts.length > 0 || readUnresolvedReferences(previewData).length > 0;
};

/** Same shape the server's `eligible_scenarios_for_course` returns, plus the position we need. */
const readItemName = (item) =>
  (typeof item?.name === "string" && item.name.trim()) ||
  (typeof item?.name_i18n?.english === "string" && item.name_i18n.english.trim()) ||
  (item?.id != null ? `Scenario ${item.id}` : "");

/** Missing order sorts LAST. `null` counts as missing — `Number(null)` is 0, which would otherwise
 *  quietly sort an unordered item to the very front. */
const orderOf = (value) =>
  value == null || !Number.isFinite(Number(value)) ? Number.MAX_SAFE_INTEGER : Number(value);

/**
 * Every scenario in the course, in learner order (unit order, then item order within the unit).
 *
 * Same-course only, matching the server: a cross-course link is rejected at save
 * (`backoffice/lessons.py`), so offering one would only produce a late error.
 */
export const collectLinkableScenarios = (course) => {
  const units = Array.isArray(course?.units) ? course.units : [];
  const options = [];
  units.forEach((unit) => {
    const unitOrder = orderOf(unit?.order);
    const unitName = readItemName(unit);
    (Array.isArray(unit?.content_items) ? unit.content_items : []).forEach((item) => {
      if (item?.content_type !== "scenario" || item?.id == null) return;
      options.push({
        id: item.id,
        name: readItemName(item),
        unitName,
        unitOrder,
        order: orderOf(item?.order),
      });
    });
  });
  return options.sort((a, b) => a.unitOrder - b.unitOrder || a.order - b.order);
};

/** Where the lesson being edited sits, so we can compare it with a candidate scenario. */
export const lessonPositionInCourse = (course, lessonId, fallbackUnitId = null) => {
  const units = Array.isArray(course?.units) ? course.units : [];
  for (const unit of units) {
    const items = Array.isArray(unit?.content_items) ? unit.content_items : [];
    const match = items.find(
      (item) => item?.content_type === "lesson" && String(item?.id) === String(lessonId)
    );
    if (match) {
      return { unitOrder: orderOf(unit?.order), order: orderOf(match?.order) };
    }
  }
  // A lesson being created has no row in the tree yet. Fall back to its parent unit so the check
  // still catches the worst case (a scenario in a LATER unit); within the unit we cannot know.
  if (fallbackUnitId != null) {
    const unit = units.find((candidate) => String(candidate?.id) === String(fallbackUnitId));
    if (unit) return { unitOrder: orderOf(unit?.order), order: Number.MAX_SAFE_INTEGER };
  }
  return null;
};

/**
 * True when linking to this scenario would deadlock the learner.
 *
 * A reflection lesson is locked until its scenario has a finished run, and the ordinary order gate
 * locks the scenario until every preceding item — the reflection lesson included — is finished.
 * Point a reflection at a scenario that comes *later* and neither can ever go first: the learner is
 * stuck with no way out.
 *
 * **ADVISORY ONLY — known and accepted, not an oversight.** This drives a warning; it does not
 * block the save, and nothing validates the link server-side or at activation.
 * An author can still save through it, and the import picker offers every same-course scenario
 * regardless of order (matching the server's `eligible_scenarios_for_course`).
 *
 * The proper fix is a preflight check at activation, which needs its own design pass: a naive
 * "linked scenario must precede the lesson" rule is invalidated by any content reorder and would
 * forbid legitimate backward cross-unit links on positional rather than semantic grounds. Deferred
 * deliberately — see `content/_reference/design-patterns.md`.
 */
export const scenarioWouldDeadlock = (scenario, lessonPosition) => {
  if (!scenario || !lessonPosition) return false;
  if (scenario.unitOrder !== lessonPosition.unitOrder) {
    return scenario.unitOrder > lessonPosition.unitOrder;
  }
  return scenario.order > lessonPosition.order;
};

export const REFLECTION_LINK_LABEL = "Reflects on role play";

export const REFLECTION_LINK_TOOLTIP =
  "Link this lesson to the role play it debriefs. The coach then receives that learner's own " +
  "transcript and skill cues, and the lesson stays locked until they finish the role play. " +
  "Leave empty for an ordinary lesson.";

export const REFLECTION_LINK_NONE_LABEL = "Not a reflection lesson";

export const REFLECTION_DEADLOCK_WARNING =
  "This role play comes after this lesson in the course. The lesson stays locked until the role " +
  "play is finished, and the role play stays locked until this lesson is — the learner would be " +
  "stuck. Pick a role play the learner reaches first.";

/** Label for one option, e.g. `Browsing for a Common Interest — Session 1`. */
export const describeScenarioOption = (option) =>
  option?.unitName ? `${option.name} — ${option.unitName}` : option?.name || "";
